<script setup>
import '@/src/vue/components/Chat/_chat.scss';
import { eventCategories, events } from '@js/utils/event';
import { isMobileView, l, logEvent } from '@js/utils/toolbox';
import { ref, useTemplateRef } from 'vue';
import { useChatApi, usePresetQueries } from './useChatApi';
import ChatHeader from './ChatHeader.vue';
import ChatInput from './ChatInput.vue';
import ChatMessages from './ChatMessages.vue';
import ChatPresetsBar from './ChatPresetsBar.vue';

const input = ref('');
const userInput = useTemplateRef('userInput');
const bottomSentinel = ref(null);

const { isEnabled, loading, sendMessage, messages, newConversation: startNewConversation } = useChatApi(true);
const { presets } = usePresetQueries();

async function send (bypassEmpty = false, source = 'input') {
	const text = input.value.trim();
	if (!text && !bypassEmpty) {
		return;
	}

	input.value = '';

	if (!isMobileView()) {
		userInput.value?.focus();
	} else {
		userInput.value?.blur();
	}

	await sendMessage(text, source);
}

function sendPreset (query) {
	input.value = query;
	send(false, 'preset');
}

function toggleFullScreen () {
	isFullScreen.value = !isFullScreen.value;
	logEvent(events.chatToggleFullscreen, eventCategories.chat, isFullScreen.value ? 'open' : 'close');
}

async function newConversation (source = 'header_button') {
	await startNewConversation(source);
}

const isFullScreen = ref(false);
</script>

<template>
  <div
    class="chat-container"
    :class="{ 'full-screen': isFullScreen }"
  >
    <ChatHeader
      :is-full-screen="isFullScreen"
    >
      <template #header-actions>
        <div class="ml-auto flex gap-2">
          <button
            class="abBtn abBtn--blueWhite white ml-auto"
            @click="toggleFullScreen"
          >
            {{ isFullScreen ? l('chat.fullscreen.close') : l('chat.fullscreen.open') }}
          </button>
          <!-- New chat button -->
          <button
            class="abBtn abBtn--blueWhite white ml-auto"
            @click="newConversation"
          >
            {{ l('chat.newChat') }}
          </button>
        </div>
      </template>
    </ChatHeader>

    <template v-if="isEnabled">
      <ChatMessages
        ref="chatMessages"
        :messages="messages"
        :loading="loading"
        :bottom-sentinel="bottomSentinel"
        @send-preset="sendPreset"
      />

      <ChatPresetsBar
        :presets="presets"
        @send-preset="sendPreset"
      />
    </template>

    <ChatDisabled v-else />

    <ChatInput
      v-model="input"
      :loading="loading"
      @submit="send"
    />
  </div>
</template>

<style scoped lang="scss">
.chat-container {
  // min-height: 0;
  height: calc(var(--window-height) - 50px);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  box-shadow: $thinBoxShadow;
  overflow: hidden;

  @include breakpoint_up(md) {
    border-radius: 16px;
    margin-top: 2rem;
    height: 78vh;
  }
}
.full-screen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  border-radius: 0 !important;
  margin: 0 !important;
  z-index: 1000;
}


</style>

<style lang="scss">
footer {
  display: none;

  @include breakpoint_up(md) {
    display: initial;
  }
}

section.container {
  @include breakpoint_down(md) {
    padding: 0 !important;
    margin: 0 !important;
  }
}
</style>
